import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAssignedTeacher,
  selectBackTeachers,
  tryAssignTeacher,
  tryGetAllTeachers,
  tryGetTeachersByCourseGroup,
  tryDisassociateTeacher,
  selectDisassociatedTeacher
} from "../../../store/slices/backoffice-teachers";
import {
  Grid,
  Typography,
  Drawer,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Chip,
  MenuItem,
  DialogActions,
  DialogTitle,
  Dialog,
  Divider,
  useTheme,
  useMediaQuery,
  Box,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { NumericFormat, PatternFormat } from "react-number-format";
import { useFormik } from "formik";
import NameDisplay from "../../layout/name-display";
import ModalMessages from "../../layout/modal-messages";
import Spinner from "../../layout/spinner";

const TEACHER_ROLES = { TITULAR: 1, ACOMPANANTE: 2 };

const validate = (values) => {
  const errors = { teachers: [] };
  let hasErrors = false;
  let titularCount = 0;

  const teacherCuils = values.teachers
    .map((t) => t.teacherCuil?.replace(/-/g, "").replace(/_/g, ""))
    .filter((cuil) => cuil && cuil.length > 0);

  const hasDuplicates = teacherCuils.some(
    (cuil, index) => teacherCuils.indexOf(cuil) !== index
  );

  if (hasDuplicates) {
    errors.general = "No puede asignar el mismo docente más de una vez.";
    hasErrors = true;
  }

  values.teachers.forEach((teacher, index) => {
    const teacherErrors = {};

    if (!teacher.teacherCuil) {
      teacherErrors.teacherCuil = "Requerido";
    } else {
      let cuilReplaced = teacher.teacherCuil.replace(/-/g, "").replace(/_/g, "");
      if (cuilReplaced.toString().length !== 11) {
        teacherErrors.cuil = "Longitud de CUIL inválida";
      }
    }

    if (!teacher.amountCommited) teacherErrors.amountCommited = "Requerido";
    if (!teacher.teacherRole) teacherErrors.teacherRole = "Requerido";

    if (teacher.teacherRole === TEACHER_ROLES.TITULAR) titularCount++;

    if (Object.keys(teacherErrors).length > 0) {
      errors.teachers[index] = teacherErrors;
      hasErrors = true;
    }
  });

  if (titularCount > 2) {
    errors.general = "Solo puede asignar hasta dos Docentes Titulares.";
    hasErrors = true;
  }

  return hasErrors ? errors : {};
};

const AssignTeacherToCourseDrawer = ({
  openDrawer,
  setOpenDrawer,
  dataDrawer,
  setOpenDialog,
}) => {
  const selectTeachers = useSelector(selectBackTeachers);
  const assignedTeacher = useSelector(selectAssignedTeacher);
  const disassociatedTeacher = useSelector(selectDisassociatedTeacher);
  const { data: teachers } = selectTeachers;

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [teachersToDelete, setTeachersToDelete] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: dataDrawer.courseId,
      courseGroupId: dataDrawer.courseGroupId,
      teachers: [],
    },
    validate,
    onSubmit: async (values) => {
      const { courseId, courseGroupId } = values;

      const mappedTeachers = values.teachers.map((t) => {
        let cuilReplaced = t.teacherCuil?.replace(/-/g, "").replace(/_/g, "");
        let foundTeacher = teachers.find((x) => x.shortDescription === cuilReplaced);

        return {
          teacherCourseId: t.teacherCourseId,
          teacherId: foundTeacher ? foundTeacher.id : 0,
          amountCommited: t.amountCommited,
          teacherRole: t.teacherRole,
        };
      });

      const batchRequest = {
        courseId,
        courseGroupId,
        teachers: mappedTeachers,
      };

      const action = await dispatch(tryAssignTeacher(batchRequest));

      if (action.payload?.data?.isValid) {
        if (teachersToDelete.length > 0) {
          const results = await Promise.allSettled(
            teachersToDelete.map((id) => dispatch(tryDisassociateTeacher(id)))
          );

          const anyFailed = results.some((result) => result.status === "rejected" || !result.value.payload?.data?.isValid);

          if (anyFailed) {
            return;
          }
        }

        setOpenDrawer(false);
        setOpenDialog(true);
      }
    },
  });

  useEffect(() => {
    const loadCurrentTeachers = async () => {
      if (openDrawer) {
        await dispatch(tryGetAllTeachers());

        if (dataDrawer.courseGroupId) {
          const action = await dispatch(
            tryGetTeachersByCourseGroup({
              courseId: dataDrawer.courseId,
              courseGroupId: dataDrawer.courseGroupId
            })
          );

          if (action.payload?.data?.isValid) {
            const currentTeachers = action.payload.data.data;
            if (currentTeachers.length > 0) {
              formik.setFieldValue("teachers", currentTeachers.map(tc => {
                const foundTeacher = teachers?.find((x) => x.id === tc.teacherId);
                return {
                  teacherCourseId: tc.teacherCourseId,
                  teacherId: tc.teacherId,
                  teacherCuil: foundTeacher ? foundTeacher.shortDescription : "",
                  amountCommited: tc.amountCommited,
                  teacherRole: tc.teacherRole || TEACHER_ROLES.TITULAR
                };
              }));
            } else {
              handleAddInitialTeacher();
            }
          } else {
            handleAddInitialTeacher();
          }
        } else {
          handleAddInitialTeacher();
        }
      }
    };

    loadCurrentTeachers();
    setTeachersToDelete([]);
  }, [openDrawer, dataDrawer.courseGroupId]);

  useEffect(() => {
    if (teachers && formik.values.teachers.length > 0) {
      const updatedTeachers = formik.values.teachers.map(t => {
        if (!t.teacherCuil && t.teacherId) {
          const foundTeacher = teachers.find(x => x.id === t.teacherId);
          if (foundTeacher) {
            return { ...t, teacherCuil: foundTeacher.shortDescription };
          }
        }
        return t;
      });

      const hasChanges = updatedTeachers.some((t, i) => t.teacherCuil !== formik.values.teachers[i].teacherCuil);
      if (hasChanges) {
        formik.setFieldValue("teachers", updatedTeachers);
      }
    }
  }, [teachers, formik.values.teachers]);

  const handleAddInitialTeacher = () => {
    formik.setFieldValue("teachers", [{
      teacherCourseId: 0,
      teacherId: "",
      teacherCuil: "",
      amountCommited: "",
      teacherRole: TEACHER_ROLES.TITULAR,
    }]);
  };

  const handleAddTeacher = () => {
    formik.setFieldValue("teachers", [
      ...formik.values.teachers,
      {
        teacherCourseId: 0,
        teacherId: "",
        teacherCuil: "",
        amountCommited: "",
        teacherRole: TEACHER_ROLES.ACOMPANANTE,
      },
    ]);
  };

  const handleRemoveTeacher = (index) => {
    const teacherToRemove = formik.values.teachers[index];

    if (teacherToRemove.teacherCourseId > 0) {
      setTeachersToDelete(prev => [...prev, teacherToRemove.teacherCourseId]);
    }

    const newTeachers = [...formik.values.teachers];
    newTeachers.splice(index, 1);
    formik.setFieldValue("teachers", newTeachers);
  };

  const handleOpenConfirmation = async () => {
    const currentErrors = await formik.validateForm();
    if (Object.keys(currentErrors).length === 0) {
      setOpenConfirmationDialog(true);
    } else {
      formik.setTouched({
        teachers: formik.values.teachers.map(() => ({
          teacherCuil: true,
          amountCommited: true,
          teacherRole: true,
        })),
      });
    }
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openDrawer}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      onClose={() => setOpenDrawer(false)}
      sx={{ zIndex: 1203, flexShrink: 0 }}
    >
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 2 },
          backgroundColor: "primary",
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            mb: { xs: 0, sm: 1 },
            pr: 4,
            color: "white"
          }}
        >
          Asignar profesor
        </Typography>

        <Typography
          variant="body2"
          align="left"
          sx={{ display: { xs: "none", sm: "block" }, color: "white", fontSize: "12px" }}
        >
          Por favor, complete los campos a continuación para asignar docentes al curso.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={2} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Typography>
              <Chip
                label={dataDrawer.briefDescription}
                color="primary"
                style={{ fontSize: 18 }}
              />
            </Typography>
            <Typography
              marginTop={1}
              style={{ color: "black", fontSize: 16 }}
            >
              Comisión: {dataDrawer.courseGroupDescription}
            </Typography>
          </Grid>

          {formik.errors.general && (
            <Grid item xs={12}>
              <Typography color="error" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                {formik.errors.general}
              </Typography>
            </Grid>
          )}

          {formik.values.teachers.map((teacher, index) => {
            const teacherErrors = formik.errors.teachers?.[index] || {};
            const teacherTouched = formik.touched.teachers?.[index] || {};

            return (
              <Grid container item xs={12} spacing={2} key={index}>
                <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="primary">
                    Docente #{index + 1}
                  </Typography>
                  {formik.values.teachers.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => handleRemoveTeacher(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`role-label-${index}`}>Rol del Docente</InputLabel>
                    <Select
                      labelId={`role-label-${index}`}
                      name={`teachers[${index}].teacherRole`}
                      label="Rol del Docente"
                      value={teacher.teacherRole}
                      onChange={(e) =>
                        formik.setFieldValue(`teachers[${index}].teacherRole`, e.target.value)
                      }
                    >
                      <MenuItem value={TEACHER_ROLES.TITULAR}>Titular</MenuItem>
                      <MenuItem value={TEACHER_ROLES.ACOMPANANTE}>Acompañante</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <InputLabel id={`teacher-label-${index}`} sx={{ fontSize: '13px' }}>Seleccione Profesor</InputLabel>
                  <Select
                    labelId={`teacher-label-${index}`}
                    name={`teachers[${index}].teacher`}
                    size="small"
                    fullWidth
                    value={teacher.teacherCuil || ""}
                    onChange={(e) => {
                      formik.setFieldValue(`teachers[${index}].teacherCuil`, e.target.value);
                    }}
                    error={Boolean(teacherTouched.teacherCuil && teacherErrors.teacherCuil)}
                  >
                    {teachers &&
                      teachers.map((x) => (
                        <MenuItem key={x.id} value={x.shortDescription}>
                          <NameDisplay>{x.description}</NameDisplay>
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <PatternFormat
                      variant="outlined"
                      label="CUIL del Profesor"
                      size="small"
                      value={teacher.teacherCuil || ""}
                      format="##-########-#"
                      mask="_"
                      customInput={TextField}
                      onChange={(e) => {
                        const { value } = e.target;
                        let cuilReplaced = value.replace(/-/g, "").replace(/_/g, "");
                        formik.setFieldValue(`teachers[${index}].teacherCuil`, cuilReplaced);
                      }}
                      error={Boolean(teacherTouched.teacherCuil && (teacherErrors.teacherCuil || teacherErrors.cuil))}
                      helperText={teacherTouched.teacherCuil && (teacherErrors.teacherCuil || teacherErrors.cuil)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <NumericFormat
                      variant="outlined"
                      label="Monto asignado al Profesor"
                      size="small"
                      thousandSeparator="."
                      decimalSeparator=","
                      isNumericString
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="$"
                      InputLabelProps={{ shrink: true }}
                      customInput={TextField}
                      value={teacher.amountCommited}
                      onValueChange={(values) => {
                        const { floatValue } = values;
                        formik.setFieldValue(`teachers[${index}].amountCommited`, floatValue);
                      }}
                      error={Boolean(teacherTouched.amountCommited && teacherErrors.amountCommited)}
                      helperText={teacherTouched.amountCommited && teacherErrors.amountCommited}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
              </Grid>
            );
          })}

          {formik.values.teachers.length < 3 && (
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Button
                startIcon={<AddIcon />}
                variant="text"
                color="primary"
                onClick={handleAddTeacher}
              >
                Agregar otro docente
              </Button>
            </Grid>
          )}

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            {isMobile ? (
              <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOpenConfirmation}
                  disabled={assignedTeacher.isLoading || formik.values.teachers.length === 0}
                >
                  {assignedTeacher.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={assignedTeacher.isLoading}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                <Button
                  variant="contained"
                  onClick={handleOpenConfirmation}
                  disabled={assignedTeacher.isLoading || formik.values.teachers.length === 0}
                  sx={{ width: 100, marginRight: 2 }}
                >
                  {assignedTeacher.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={assignedTeacher.isLoading}
                  sx={{ border: 2, width: 100 }}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Grid>

        </Grid>
      </Box>

      {assignedTeacher.errors && assignedTeacher.errors.length > 0 && (
        <ModalMessages errors={assignedTeacher.errors} />
      )}

      {disassociatedTeacher.errors && disassociatedTeacher.errors.length > 0 && (
        <ModalMessages errors={disassociatedTeacher.errors} />
      )}

      {openConfirmationDialog && (
        <Dialog
          open={openConfirmationDialog}
          aria-labelledby="alert-dialog-title"
        >
          <DialogTitle id="alert-dialog-title">
            {"¿Confirmas asignar a los profesores?"}
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                formik.handleSubmit();
                setOpenConfirmationDialog(false);
              }}
              color="primary"
            >
              Aceptar
            </Button>
            <Button
              onClick={() => setOpenConfirmationDialog(false)}
              color="error"
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Drawer>
  );
};

export default AssignTeacherToCourseDrawer;